import React, { Suspense, lazy } from "react";
import { Container, Col, Row } from "reactstrap";
import { Fields } from "@repoflow/components/fields";
import { useSectionContext } from "@repoflow/components/section";
import { Link } from "react-router-dom";
import { List } from "@repoflow/components/list-cursor";
import * as Loading from "@repoflow/components/loading";
import * as PointLimitQL from "queries/point-limit";
import PointLimitSchema from "@common/schema/point-limit/public";
import * as widgets from "widgets/register";

// recharts and its d3 tree are ~100KB gzipped. Lazily loaded here and on the
// statistics screen so the points LIST — which links to neither — never pays for
// them, the same way bucketws' and auth's do it.
const PointCharts = lazy(() => import("../captures/charts"));

export const Sidebar = () => null;

export const Indice = () => null;

// The point, and the summary of what it has done. Nothing to explore from.
//
// RESTRUCTURED 2026-08-13. This page used to stack four blocks: the point's
// fields, a list of its limits, the usage table, and a list of its recent
// captures. Two of those were listings that existed one click from a screen
// dedicated to each of them, and between them they made the page a full explorer
// nobody had asked to open — the limits list duplicated /limit, and the captures
// list duplicated the first page of /capture with fewer columns.
//
// Both went. The captures list stayed gone; **the limits list came back the same
// day**, and the distinction is the useful part of this note. A captures listing
// grows without bound and the screen it duplicated is a real explorer with a
// range picker and filters. A point's limits are a handful of rows that never
// grow with use, they are the point's configuration rather than its data, and
// what they declare is the first thing anybody opens this page to check. "It is
// also on another screen" was the wrong test to apply to them.
//
// What is left is the shape bucketws' bucket page has: the entity, its limits,
// and one Statistics block answering "is anything wrong here" in a glance.
//
// TWO FURTHER CHANGES ON 2026-08-13, after that restructure was looked at:
//
//   The usage bars are gone from this page, and the table they summarised is
//   gone from /capture. What they drew is one row per limit VALUE, and a limit
//   is scoped by whichever dimensions it names — so on a point whose dimension
//   is `orgId` that is a row per organization, growing without bound as the
//   platform does. Nothing about a summary block survives a listing that long.
//   The read behind them (`points.get.usage`) is untouched and still answers on
//   the api; what it lost is its reader in this app.
//
//   The block moved to the END of the page. Everything above it is what the
//   page is opened to do — read the point, edit it, read and add its limits —
//   and the statistics are the thing you drop to when one of those looks wrong.
//
// So nothing on this page is an absolute any more. Every figure in the block is
// a total over the last 24 hours, which is what the server defaults to when no
// filter is sent, and a measure summed over a window is a net change on any
// service that negates on removal.
export const Content = ({
  match: {
    params,
    params: { pointId },
  },
}) => {
  const { values, schema, base } = useSectionContext();

  return (
    <>
      <Container className="mt-4">
        <Row>
          <Col>
            <div className="float-right">
              <Link className="btn btn-primary" to={`${base}/edit`}>
                Edit
              </Link>{" "}
              <Link className="btn btn-danger" to={`${base}/remove`}>
                Remove
              </Link>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Fields
              schema={schema}
              values={values}
              widgets={widgets}
              target={{ $ref: "/Public/Point" }}
              config={{
                "/Public/Point": {
                  fields: ["pointId", "description", "dimensions", "measures"],
                },
              }}
            />
          </Col>
        </Row>
      </Container>

      <Container>
        <div className="mt-4"></div>

        <Row>
          <Col>
            <div className="float-right">
              <Link to={`${base}/limit`}>Go to Limits...</Link>
            </div>
            <h5 className="d-inline-block">Limits</h5>
          </Col>
        </Row>

        <div className="mt-3"></div>

        <Row>
          <Col>
            <Link className={"btn btn-primary"} to={`${base}/limit/new`}>
              <i className="fa fa-plus" /> Add Limit
            </Link>
          </Col>
        </Row>

        <div className="mt-3"></div>

        <Row>
          <Col>
            {/* The listing, back on the page as of 2026-08-13.

                Taking it off was the one part of the restructure above that did
                not survive contact: a limit is not a listing the way captures
                are. A point carries a handful of them, they are the point's
                CONFIGURATION rather than its data, and "which limits does this
                point have, and over what" is the question the page is opened
                with — not something to click through to. The captures listing
                stays gone for the opposite reason: it grows without bound and
                the screen it duplicated is a real explorer.

                `limits` is in the columns deliberately. That is the component
                list — {measure, type, period} — and it is what distinguishes
                two limits on the same point from each other; a table of
                descriptions and dates cannot answer what the limit limits.

                Its own schema import rather than the section's: this renders
                /Public/PointLimit, which is not the type this section is
                mounted on, and the sibling screen at /limit reads it the same
                way. */}
            <List
              path={"viewer.account.points.get.limits"}
              query={PointLimitQL.list}
              variables={params}
              schema={PointLimitSchema}
              widgets={widgets}
              target={{ $ref: "/Public/PointLimit" }}
              empty={"No limits yet."}
              config={{
                "/Public/PointLimit": {
                  fields: ["description", "dimensions", "limits", "updatedAt"],
                },
              }}
              content={({ item: { limitId } }) => (
                <Link className={"btn btn-primary"} to={`${base}/limit/${limitId}/`}>
                  View
                </Link>
              )}
            />
          </Col>
        </Row>
      </Container>

      <Container>
        <div className="mt-4"></div>

        <Row>
          <Col>
            <div className="float-right">
              <Link to={`${base}/capture`}>Go to Statistics...</Link>
            </div>
            <h5>Statistics</h5>
          </Col>
        </Row>

        <div className="mt-3"></div>

        <Row>
          <Col>
            {/* The full width of the page, and `compact` — the KPI tiles and the
                time charts, without the by-event-type breakdown or the table
                toggle that opens the explorer's bin table.

                No filter argument either: the server defaults to the last 24
                hours, which is the right window for an overview. The screen one
                link away is the same charts under a range picker. */}
            <Suspense fallback={<Loading.Content />}>
              <PointCharts
                pointId={pointId}
                measures={values?.measures || []}
                compact
                rangeLabel={"Last 24 hours"}
              />
            </Suspense>
          </Col>
        </Row>
      </Container>
    </>
  );
};
